import type { AddressDto, BackgroundReportPdf, CriminalRecordPdf } from '@zg-rentals/ts-capps-client';
import { CicBackgroundReportStatus } from '@zg-rentals/ts-capps-client';

export function getFullNameForBackgroundReport({
  firstName,
  middleName,
  lastName,
}: {
  firstName?: string;
  middleName?: string;
  lastName?: string;
}) {
  if (!firstName && !lastName && !middleName) {
    return 'name unavailable';
  }
  return `${firstName} ${middleName ? `${middleName} ` : ''}${lastName}`;
}

/**
 * Returns the description of the report status if the status implies an
 * unavailable report. Returns null for AVAILABLE status.
 */
export function getReportStatusDescription(reportStatus: CicBackgroundReportStatus | string | null | undefined) {
  if (reportStatus === CicBackgroundReportStatus.AVAILABLE) {
    return null;
  }
  if (
    reportStatus == null ||
    [
      CicBackgroundReportStatus.SERVICE_FAILED,
      CicBackgroundReportStatus.IN_DISPUTE,
      CicBackgroundReportStatus.MANUAL_REVIEW,
    ].includes(reportStatus as CicBackgroundReportStatus)
  ) {
    return 'Pending';
  }
  if (
    [CicBackgroundReportStatus.INVALID_SSN, CicBackgroundReportStatus.INVALID_DOB].includes(
      reportStatus as CicBackgroundReportStatus,
    )
  ) {
    return 'Error';
  }

  // Unidentified bug non-available status
  return 'Unavailable';
}

export function getIsEvictionReportUnavailable(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.evictionReport?.reportStatus !== CicBackgroundReportStatus.AVAILABLE;
}

export function getIsEvictionReportInDispute(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.evictionReport?.reportStatus === CicBackgroundReportStatus.IN_DISPUTE;
}

export function getIsEvictionReportFrozen(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.evictionReport?.reportStatus === CicBackgroundReportStatus.SECURITY_FREEZE;
}

export function getIsEvictionReportInManualReview(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.evictionReport?.reportStatus === CicBackgroundReportStatus.MANUAL_REVIEW;
}

export function getIsEvictionReportRegulatoryRestrictions(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.evictionReport?.reportStatus === CicBackgroundReportStatus.REGULATORY_RESTRICTIONS;
}

export function getIsCriminalReportUnavailable(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.criminalReport?.reportStatus !== CicBackgroundReportStatus.AVAILABLE;
}

export function getIsCriminalReportInDispute(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.criminalReport?.reportStatus === CicBackgroundReportStatus.IN_DISPUTE;
}

export function getIsCriminalReportFrozen(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.criminalReport?.reportStatus === CicBackgroundReportStatus.SECURITY_FREEZE;
}

export function getIsCriminalReportInManualReview(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.criminalReport?.reportStatus === CicBackgroundReportStatus.MANUAL_REVIEW;
}

export function getIsCriminalReportRegulatoryRestrictions(backgroundReport: BackgroundReportPdf) {
  return backgroundReport?.criminalReport?.reportStatus === CicBackgroundReportStatus.REGULATORY_RESTRICTIONS;
}

export function isSexOffenderRecord({ subject: { category } }: CriminalRecordPdf) {
  return category && category.toLowerCase() === 'sex offender';
}

export function getZipCodeScreenFormat(zip?: string | null): string {
  if (!zip) {
    return '';
  }

  if (zip.length === 5) {
    return zip;
  }

  if (zip.length === 10 && zip[5] === '-') {
    return zip;
  }

  const formattedZip = `${zip.slice(0, 5)}-${zip.slice(5)}`;
  return formattedZip;
}

export function getFormattedAddress(address?: Partial<AddressDto> | null) {
  if (!address) {
    return '';
  }
  const { street = '', city = '', state = '', zip = '', unit = '' } = address;
  const formattedZip = getZipCodeScreenFormat(zip as string);

  const streetUnit = [street, unit].filter(Boolean).join(' ');
  const stateZip = [state, formattedZip].filter(Boolean).join(' ');

  return [streetUnit, city, stateZip].filter(Boolean).join(', ');
}

export function pluralize(string: string, unit: number, skipSingularUnit = false) {
  return [unit == 1 && skipSingularUnit ? '' : unit, `${string}${unit > 1 ? 's' : ''}`].filter(Boolean).join(' ');
}

export function getReportIdsText(responseIds: Array<string | undefined> = []) {
  return responseIds
    .filter((id) => !!id)
    .map((id) => `Z-${id}`)
    .join(', ');
}
